import {
    Badge, Button, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme
} from "@mui/material";
import {
    IconGridDots,
    IconFilter,
    IconPlaylistAdd,
    IconSortAscending2,
    IconSortDescending2
} from "@tabler/icons-react";
import { getPager } from "helper/common";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
    clearUserList,
    closeUserFilterDialog,
    closeUserCreateDialog,
    getUserList,
    openUserFilterDialog,
    openUserCreateDialog,
    setUserFilteredData,
    userListErrorClear
} from "store/app/user-management/actions";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import CreateUser from "./create";
import FilterUsers from "./filter";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";

const tableHeaderStyle = { paddingLeft: '0px' }

const UsersList = (props) => {
    //--------------------List--------------------------------------
    const { userList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Filter------------------------------------
    const { isUserFilterDialogOpen, userFilterData } = props;
    //--------------------Create------------------------------------
    const { isUserCreateDialogOpen } = props;
    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    useEffect(() => {
        props.userListErrorClear();

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);
        return () => {
            props.clearUserList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    const handleSort = (column) => {
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    const setPage = (page = 1) => {
        props.getUserList(page, pageSize, sortOrder, sortDirection, userFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [userFilterData, sortOrder, sortDirection, pageSize])

    const handleUserCreateOpen = () => {
        props.openUserCreateDialog();
    }
    const handleUserCreateClose = () => {
        props.closeUserCreateDialog();
    }

    const handleFilterOpen = () => {
        props.openUserFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeUserFilterDialog();
    }

    return (
        <>
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Users List - </Typography>
                    <Tooltip title="User Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={userFilterData ? Object.keys(userFilterData).length : 0}
                        color="secondary"
                        invisible={userFilterData ? Object.keys(userFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleUserCreateOpen}
                            startIcon={<IconPlaylistAdd />}
                        >
                            Create User
                        </Button>
                    }
                </Grid>
            </SubCard>
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert type={'error'} content={errorMessage} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('DisplayName')}
                                        endIcon={sortOrder === 'DisplayName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Display Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('UserRoleName')}
                                        endIcon={sortOrder === 'UserRoleName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Role &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsActive')}
                                        endIcon={sortOrder === 'IsActive' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        User Status
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsEnabled')}
                                        endIcon={sortOrder === 'IsEnabled' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Account Status
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Email')}
                                        endIcon={sortOrder === 'Email' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Email Address
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('FirstName')}
                                        endIcon={sortOrder === 'FirstName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        First Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('LastName')}
                                        endIcon={sortOrder === 'LastName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Last Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('MobileNumber')}
                                        endIcon={sortOrder === 'MobileNumber' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Mobile Number
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('AssignedStatesCount')}
                                        endIcon={sortOrder === 'AssignedStatesCount' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Assigned States/Exports Count
                                    </Button>
                                </TableCell>
                                <TableCell>View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(userList?.length > 0) && !isPending ?
                                userList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{data.DisplayName || 'N/A'}</TableCell>
                                        <TableCell>{data.UserRoleName || 'N/A'}</TableCell>
                                        <TableCell>
                                            {data.IsActive ?
                                                <Chip label={"Active"} chipcolor="success" size="small" />
                                                : <Chip label={"Inactive"} chipcolor="error" size="small" />
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {data.IsEnabled ?
                                                <Chip label={"Enabled"} chipcolor="success" size="small" />
                                                : <Chip label={"Disabled"} chipcolor="error" size="small" />
                                            }
                                        </TableCell>
                                        <TableCell>{data.Email || 'N/A'}</TableCell>
                                        <TableCell>{data.FirstName || 'N/A'}</TableCell>
                                        <TableCell>{data.LastName || 'N/A'}</TableCell>
                                        <TableCell>{data.MobileNumber || 'N/A'}</TableCell>
                                        <TableCell>{data.AssignedStatesCount || '0'}</TableCell>
                                        <TableCell>
                                            <Link to={`/users/details/${data.Id}`}>
                                                <IconGridDots color={theme.palette.primary.main} />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={9} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    : <TableCell colSpan={9} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {(totalCounts > 0) &&
                    <Grid>
                        <VWPagination
                            pageDetails={Pager}
                            pageChange={setPage}
                            totalRecords={totalCounts}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    </Grid>
                }
            </MainCard>
            <CreateUser
                filter={userFilterData}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isUserCreateDialogOpen}
                onClose={handleUserCreateClose}
            />
            <FilterUsers
                open={isUserFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    userList: state.users.userList,
    totalCounts: state.users.userListCount,
    isPending: state.users.isUserListPending,
    errorMessage: state.users.userListErrorMessage,
    //--------------------Filter------------------------------------
    isUserFilterDialogOpen: state.users.isUserFilterDialogOpen,
    userFilterData: state.users.userFilterData,
    //--------------------Create------------------------------------
    isUserCreateDialogOpen: state.users.isUserCreateDialogOpen,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getUserList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getUserList(page, pageSize, sortOrder, sortDirection, filter)),
    clearUserList: () => dispatch(clearUserList()),
    userListErrorClear: () => dispatch(userListErrorClear()),
    //--------------------Filter------------------------------------
    openUserFilterDialog: () => dispatch(openUserFilterDialog()),
    closeUserFilterDialog: () => dispatch(closeUserFilterDialog()),
    setUserFilteredData: (filter) => dispatch(setUserFilteredData(filter)),
    //--------------------Create------------------------------------
    openUserCreateDialog: () => dispatch(openUserCreateDialog()),
    closeUserCreateDialog: () => dispatch(closeUserCreateDialog()),

})
export default connect(mapStateToProps, mapDispatchToProps)(UsersList);